import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import App from "./App.tsx";
import "./index.css";
import { initGA, initClarity } from "./lib/analytics";
import { initPostHog } from "./lib/posthog";
import { captureInstallPrompt } from "./lib/install-prompt";

const isSupabaseAuthLockError = (event: {
  exception?: { values?: Array<{ type?: string; value?: string }> };
}) => {
  return (event.exception?.values ?? []).some(
    (ex) =>
      ex.type === "AbortError" &&
      ex.value?.includes("Lock broken by another request with the 'steal' option")
  );
};

// lazyWithRefresh auto-reloads once (sessionStorage-gated) on any chunk load failure.
// All "Importing a module script failed" errors are pure noise — the UX is already handled.
const isChunkImportFailure = (event: {
  exception?: { values?: Array<{ value?: string }> };
}) =>
  (event.exception?.values ?? []).some((ex) =>
    ex.value?.includes("Importing a module script failed")
  );

// A tab opened before a deploy asks for chunk files that no longer exist (each deploy renames them).
// lazyWithRefresh covers routes; this covers every other import() (Share image, canvas code, ...).
// The error is never cancelled (preventDefault would make Vite resolve the import with undefined,
// which lazyWithRefresh would count as a success and reset its own retry gate). Instead the reload
// is gated by time: if this page already reloaded for a missing chunk in the last 30s, the chunk is
// really broken, so the error shows as normal instead of reloading in a loop.
window.addEventListener("vite:preloadError", () => {
  const last = Number(sessionStorage.getItem("preload-reload-at") ?? "0");
  if (Date.now() - last < 30_000) return;
  sessionStorage.setItem("preload-reload-at", String(Date.now()));
  window.location.reload();
});

const sentryDsn =
  import.meta.env.VITE_SCOREGLASS_SENTRY_DSN?.trim() ||
  import.meta.env.VITE_SENTRY_DSN?.trim();
const isProd = import.meta.env.PROD;
const sentrySendDefaultPii =
  import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII?.toLowerCase() === "true";
const enableVercelTelemetry =
  import.meta.env.VITE_ENABLE_VERCEL_TELEMETRY?.toLowerCase() !== "false";

// Chrome fires beforeinstallprompt once per page load, often before any component mounts.
captureInstallPrompt();
initGA();
initClarity();
initPostHog();

if (isProd && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/nzcwflgmfjeqgoccxfwz\.supabase\.co\/(rest|functions|rpc)/,
    ],
    // Errors injected by browser extensions are not actionable — filter by stack URL.
    denyUrls: [/chrome-extension:\/\//, /moz-extension:\/\//],
    sendDefaultPii: sentrySendDefaultPii,
    beforeSend(event) {
      if (isChunkImportFailure(event)) {
        return null;
      }
      if (isSupabaseAuthLockError(event)) {
        return null;
      }
      return event;
    },
  });
}

if (isProd && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Install-eligibility is a nice-to-have, not required for the app to function.
    });
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
    {enableVercelTelemetry ? <Analytics /> : null}
    {enableVercelTelemetry ? <SpeedInsights /> : null}
  </StrictMode>
);
